import React, { useMemo, useState } from "react";
import styled from "styled-components";

import styledTheme from "styled-theming";
import {
  Avatar,
  ClickAwayListener,
  Icon,
  ListItem,
  ListItemText,
} from "@mui/material";
import { usePortal } from "../../../Providers/PortalProvider";
import { Trans, useTranslation } from "react-i18next";
import { useShoppingCart } from "../../providers/ShoppingCartProvider";
import useId from "../../../hooks/useId";
import Radio from "@mui/material/Radio";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme) => ({
  root: {
    fontSize: "16px",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  secondaryText: {
    fontSize: "12px",
  },
  avatar: {
    marginRight: theme.spacing(2),
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  listItem: {
    borderRadius: theme.spacing(1),
    "&:hover, &:focus": {
      backgroundColor: theme.palette.secondary.main + "2b",
    },
  },
  listItemInDropDown: {
    borderRadius: 0,
  },
  listItemRadioButton: {
    justifyContent: "flex-end",
  },
}));

const currentSubjectTextColor = styledTheme.variants("mode", "variant", {
  primary: {
    light: (props) => props.theme.colors.primary.main.contrastColor,
    dark: (props) => props.theme.colors.primary.main.contrastColor,
  },
});

const currentSubjectBackgroundColor = styledTheme.variants("mode", "variant", {
  primary: {
    light: (props) => props.theme.colors.primary.main.color,
    dark: (props) => props.theme.colors.primary.main.color,
  },
});

const currentSeparatorColor = styledTheme.variants("mode", "variant", {
  primary: {
    light: (props) => props.theme.colors.primary.light.color,
    dark: (props) => props.theme.colors.primary.light.color,
  },
});

const SubjectSelectorContainer = styled.div`
  position: relative;
  display: flex;
`;

console.log("Subject text color", currentSubjectTextColor);

const CurrentSubject = styled.button`
  background-color: ${(props) =>
    !props.isOpen ? currentSubjectBackgroundColor : "white"};
  color: ${(props) =>
    props.isOpen
      ? props.theme.colors.primary.dark.color
      : currentSubjectTextColor};
  padding-left: 16px;
  padding-right: 4px;
  height: 36px;
  line-height: 36px;
  border-radius: 4px;
  border-bottom-left-radius: ${(props) => (props.isOpen ? 0 : "4px")};
  border-bottom-right-radius: ${(props) => (props.isOpen ? 0 : "4px")};
  margin-bottom: 16px;
  font-weight: bold;
  position: relative;
  display: flex;
  flex-grow: 1;
  border: none;
  text-align: left;

  cursor: pointer;

  :hover {
    background-color: ${(props) =>
      !props.isOpen ? props.theme.colors.primary.main.hoverColor : "white"};
    color: ${(props) =>
      props.isOpen
        ? props.theme.colors.primary.dark.color
        : props.theme.colors.primary.main.hoverContrastColor};
  }

  > .subject-info {
    flex: 1;
    width: 0;
    flex-grow: 1;
    text-overflow: ellipsis;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
  }

  > .separator {
    width: 2px;
    height: 24px;
    background-color: ${currentSeparatorColor};
    margin: 6px 2px 6px 6px;
  }

  > .material-icons {
    flex: 0;
    line-height: 36px;
    font-weight: bold;
  }
`;

const OpenSubjectListContainer = styled.div`
  background-color: white;
  position: absolute;
  top: 0;
  left: 0;
  width: 312px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
  border-radius: 4px;

  .open-subject-list-current-container {
    display: flex;
  }
`;

const SubjectListContainer = styled.div`
  max-height: 528px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    height: 16px;
    width: 16px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.5);
  }

  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.primary.main.color};
    border-radius: 4px;
  }
`;

const StyledSubjectList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const StyledSubjectListItem = styled.li`
  display: flex;
  flex-direction: row;
  padding: 8px 16px 8px 16px;
  align-items: center;
  cursor: ${(props) => (props.selectable ? "pointer" : "auto")};
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  border-radius: ${(props) => (props.inDropDown ? "0" : "8px")};

  &:hover {
    background-color: ${(props) =>
      props.selectable
        ? props.theme.colors.secondary.main.color + "2b"
        : "white"};
  }

  &.extra-margin {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  opacity: ${(props) => (props.selectable ? 1 : 0.5)};
`;

console.log("Styled theme", styledTheme);

const StyledMiniIcon = styled.div`
  position: absolute;
  left: -10px;
  top: -10px;
  background-color: blue;
  height: 28px;
  width: 28px;
  border-radius: 14px;
  border: 2px solid white;
  box-sizing: border-box;
  line-height: 26px;
  background-color: ${(props) => props.theme.colors.secondary.main.color};
  color: ${(props) => props.theme.colors.secondary.main.contrastColor};
  text-align: center;

  i {
    font-size: 15px;
    line-height: 26px;
  }
`;

const PhotographerLogo = styled.img`
  object-fit: contain;
  object-position: center center;
  width: 74px;
  height: 40px;
  margin-right: 20px;
`;

const PhotographerInfo = styled.div`
  flex-grow: 1;
`;

const PhotographerName = styled.div`
  font-size: 16px;
`;

const PhotographerCity = styled.div``;

const PhotographerPictureSets = styled.div``;

const ChangePhotographerWarningContainer = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;

  > div {
    font-size: 12px;
    margin-right: 20px;
  }

  > .material-icons:first-child {
    margin-right: 20px;
    margin-left: 10px;
  }

  > .material-icons {
    color: ${(props) => props.theme.colors.primary.main.color};
  }
`;

const SubjectYearList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const SubjectYearListItem = styled.li``;

export const StyledSubjectYearListHeader = styled.h6`
  margin-top: 8px;
  overflow: hidden;
  text-align: center;
  font-size: 12px;

  > span {
    position: relative;
    display: inline-block;
    color: ${(props) => props.theme.colors.primary.dark.color};

    &:before,
    &:after {
      content: "";
      position: absolute;
      top: 50%;
      border-bottom: 2px solid;
      width: 120px; /* half of limiter*/
      margin: 0 20px;
      color: ${(props) => props.theme.colors.primary.light.color};
    }

    &:before {
      right: 100%;
    }

    &:after {
      left: 100%;
    }
  }
`;

const LoadingContainer = styled.div`
  padding: 20px;
`;

const PhotographerSeparator = styled.div`
  border-bottom: 2px solid ${(props) => props.theme.colors.primary.light.color};
`;

export default ({ currentImageSet, imageSets, onChangeImageSet }) => {
  const [showSubjectList, setShowSubjectList] = useState();

  const handleChangeImageSet = (imageSet) => {
    setShowSubjectList(false);
    onChangeImageSet(imageSet);
  };

  const handleClose = (e) => {
    setShowSubjectList(false);
    e.stopPropagation();
  };

  console.log("Render image sets", imageSets);

  const containerStyle = showSubjectList ? { zIndex: 1005 } : null;

  return (
    <ClickAwayListener onClickAway={() => setShowSubjectList(false)}>
      <SubjectSelectorContainer
        onClick={() => setShowSubjectList(true)}
        style={containerStyle}
      >
        <CurrentSubject variant="primary" isOpen={false} id="subject-selector">
          <div className="subject-info">
            {currentImageSet.subject.name}
            {currentImageSet.subject.group && (
              <React.Fragment>
                {" "}
                - {currentImageSet.subject.group}{" "}
              </React.Fragment>
            )}
          </div>
          {imageSets.length > 0 && (
            <React.Fragment>
              <div className="separator" />
              <div className="material-icons">
                {showSubjectList ? "expand_less" : "expand_more"}
              </div>
            </React.Fragment>
          )}
        </CurrentSubject>
        {showSubjectList && (
          <PhotographerOrSubjectListWrapper
            currentImageSet={currentImageSet}
            imageSets={imageSets}
            handleClose={handleClose}
            onChangeImageSet={handleChangeImageSet}
            inDropDown={true}
          />
        )}
      </SubjectSelectorContainer>
    </ClickAwayListener>
  );
};

export const PhotographerOrSubjectListWrapper = ({
  currentImageSet,
  imageSets,
  onChangeImageSet,
  handleClose,
  inDropDown,
}) => {
  const portal = usePortal();
  const { items } = useShoppingCart();

  if (portal.options.whiteLabelDisableMixPhotographers && items.length > 0) {
    return (
      <PhotographerOrSubjectList
        currentImageSet={currentImageSet}
        onChangeImageSet={onChangeImageSet}
        imageSets={imageSets}
        inDropDown={inDropDown}
        handleClose={handleClose}
      />
    );
  }

  if (inDropDown) {
    return (
      <SubjectListInContainer
        currentImageSet={currentImageSet}
        onChangeImageSet={onChangeImageSet}
        imageSets={imageSets}
        handleClose={handleClose}
        inDropDown={inDropDown}
      />
    );
  } else {
    return (
      <SubjectList
        currentImageSet={currentImageSet}
        imageSets={imageSets}
        onChangeImageSet={onChangeImageSet}
        inDropDown={inDropDown}
      />
    );
  }
};

const PhotographerOrSubjectList = ({
  currentImageSet,
  imageSets,
  onChangeImageSet,
  handleClose,
  inDropDown,
}) => {
  const [photographer, setPhotographer] = useState(null);
  const { items, loading } = useShoppingCart();

  const photographersInCart = useMemo(() => {
    if (!items) {
      return [];
    }
    return (
      items?.reduce((photographers, item) => {
        if (!item.photographers) {
          return photographers;
        }
        for (let itemPhotographer of item.photographers) {
          console.log("Item photographer", itemPhotographer);
          let photographer = photographers.find(
            (photographer) => photographer.uuid == itemPhotographer.uuid,
          );
          if (!photographer) {
            photographers.push({ ...itemPhotographer });
          }
        }
        return photographers;
      }, []) || []
    );
  }, [items]);

  const photographers = imageSets.reduce((photographers, imageSet) => {
    if (!imageSet.photographer) {
      return photographers;
    }
    let photographer = photographers.find(
      (photographer) => photographer.uuid == imageSet.photographer.uuid,
    );
    if (!photographer) {
      photographer = {
        ...imageSet.photographer,
        numPictureSets: 0,
        isSelectable:
          photographersInCart.length == 0 ||
          photographersInCart.find(
            (p) => p.uuid == imageSet.photographer.uuid,
          ),
      };
      photographers.push(photographer);
    }
    photographer.numPictureSets++;
    return photographers;
  }, []);

  console.log("The photographers", photographers);

  const filteredImageSets = useMemo(() => {
    if (!photographer) {
      return imageSets;
    }
    return imageSets.filter(
      (imageSet) => imageSet.photographer.uuid == photographer.uuid,
    );
  }, [imageSets, photographer]);

  if (loading) {
    return (
      <LoadingContainer>
        <Trans>Loading...</Trans>
      </LoadingContainer>
    );
  }

  if (photographers.length > 1 && !photographer) {
    const selectPhotographer = (
      <SelectPhotographer
        handleSetPhotographer={setPhotographer}
        photographers={photographers}
        showWarning={photographersInCart.length > 0}
        inDropDown={inDropDown}
        handleClose={handleClose}
      />
    );
    if (inDropDown) {
      return (
        <OpenSubjectListContainer>
          <div className="open-subject-list-current-container">
            {selectPhotographer}
          </div>
        </OpenSubjectListContainer>
      );
    } else {
      return <div>{selectPhotographer}</div>;
    }
  }
  if (inDropDown) {
    return (
      <SubjectListInContainer
        currentImageSet={currentImageSet}
        onChangeImageSet={onChangeImageSet}
        imageSets={filteredImageSets}
        handleClose={handleClose}
        inDropDown={inDropDown}
      />
    );
  }
  return (
    <SubjectList
      currentImageSet={currentImageSet}
      imageSets={filteredImageSets}
      onChangeImageSet={onChangeImageSet}
      inDropDown={inDropDown}
    />
  );
};

const SubjectListInContainer = ({
  currentImageSet,
  onChangeImageSet,
  imageSets,
  handleClose,
  inDropDown,
}) => {
  return (
    <OpenSubjectListContainer>
      <div className="open-subject-list-current-container">
        <CurrentSubject variant="primary" isOpen={true} onClick={handleClose}>
          <div className="subject-info">
            {currentImageSet.subject.name}
            {currentImageSet.subject.group && (
              <React.Fragment>
                {" "}
                - {currentImageSet.subject.group}{" "}
              </React.Fragment>
            )}
          </div>
          {imageSets.length > 0 && inDropDown && (
            <React.Fragment>
              <div className="material-icons">expand_less</div>
            </React.Fragment>
          )}
        </CurrentSubject>
      </div>
      <SubjectListContainer>
        <SubjectList
          currentImageSet={currentImageSet}
          imageSets={imageSets}
          onChangeImageSet={onChangeImageSet}
          inDropDown={inDropDown}
        />
      </SubjectListContainer>
    </OpenSubjectListContainer>
  );
};

const SelectPhotographer = ({
  photographers,
  handleSetPhotographer,
  handleClose,
  showWarning,
  inDropDown,
}) => {
  const { t } = useTranslation();
  console.log("Photographers", photographers, handleClose);
  return (
    <div>
      {showWarning && (
        <ChangePhotographerWarningContainer onClick={handleClose}>
          <Icon>warning</Icon>
          <div>
            <Trans>
              Unfortunately, you cannot mix photos across photographers. You
              must either complete your current order, or empty the shopping
              cart
            </Trans>
          </div>
          {inDropDown && <Icon>expand_less</Icon>}
        </ChangePhotographerWarningContainer>
      )}
      <SubjectYearList>
        {photographers.map((photographer) => {
          console.log("Photographer", photographer);
          return (
            <>
              <PhotographerSeparator />
              <StyledSubjectListItem
                onClick={(e) => {
                  if (photographer.isSelectable) {
                    console.log(this);
                    handleSetPhotographer(photographer);
                    e.stopPropagation();
                  }
                }}
                selectable={photographer.isSelectable}
                className="extra-margin"
              >
                <PhotographerLogo src={photographer.logo.url} />
                <PhotographerInfo>
                  <PhotographerName>{photographer.name}</PhotographerName>
                  <PhotographerCity>
                    {photographer.address.city}
                  </PhotographerCity>
                  <PhotographerPictureSets>
                    {t("%d picture set(s)", {
                      postProcess: "sprintf",
                      sprintf: [photographer.numPictureSets],
                    })}
                  </PhotographerPictureSets>
                </PhotographerInfo>
                <Radio checked={false} />
              </StyledSubjectListItem>
            </>
          );
        })}
      </SubjectYearList>
    </div>
  );
};

export const SubjectList = ({
  currentImageSet,
  imageSets,
  onChangeImageSet,
  inDropDown,
}) => {
  console.log("Image sets", imageSets);

  if (!imageSets) {
    return <></>;
  }

  const years = imageSets.reduce((years, imageSet) => {
    const currentYear = new Date(
      imageSet.job.creationTime * 1000,
    ).getFullYear();
    let year = years.find((y) => y.year == currentYear);
    if (!year) {
      year = {
        year: currentYear,
        imageSets: [],
      };
      years.push(year);
    }
    year.imageSets.push(imageSet);

    return years;
  }, []);

  return (
    <SubjectYearList>
      {years.map((year) => {
        return (
          <SubjectYearListItem key={year.year}>
            <SubjectYearListHeader header={year.year} />
            <List>
              {year.imageSets.map((imageSet) => {
                const currentSubject =
                  imageSet.subject.uuid == currentImageSet?.subject?.uuid;
                let selectable = isSelectable(imageSet);
                if (imageSet.disabled) {
                  selectable = false;
                }

                return (
                  <SubjectListItem
                    inDropDown={inDropDown}
                    onChangeImageSet={onChangeImageSet}
                    selectable={selectable}
                    primaryLabel={imageSet.subject.name}
                    secondaryLabel={imageSet.subject.group}
                    imageSet={imageSet}
                    isSelected={currentSubject}
                    key={imageSet.subject.uuid}
                  />
                );
              }, this)}
            </List>
          </SubjectYearListItem>
        );
      })}
    </SubjectYearList>
  );
};

export const SubjectYearListHeader = ({ header }) => {
  return (
    <StyledSubjectYearListHeader>
      <span>{header}</span>
    </StyledSubjectYearListHeader>
  );
};

export const SubjectListItem = ({
  inDropDown,
  primaryLabel,
  secondaryLabel,
  onChangeImageSet,
  selectable,
  isSelected,
  imageSet,
  icon,
}) => {
  const classes = useStyles();

  const labelId = useId();
  return (
    <ListItem
      button
      disabled={!selectable}
      onClick={(e) => {
        if (selectable) {
          console.log(this);
          onChangeImageSet(imageSet);
          e.stopPropagation();
        }
      }}
      classes={{
        root: `${classes.listItem} ${inDropDown ? classes.listItemInDropDown : ""}`,
      }}
    >
      <AvatarWithIcon icon={icon} imageSet={imageSet} />

      <ListItemText
        id={labelId}
        primary={primaryLabel}
        secondary={secondaryLabel}
        classes={{
          root: classes.root,
          secondary: classes.secondaryText,
        }}
      />

      <ListItemIcon className={classes.listItemRadioButton}>
        <Radio
          checked={isSelected}
          tabIndex={-1}
          disableRipple
          inputProps={{ "aria-labelledby": labelId }}
          color="secondary"
        />
      </ListItemIcon>
    </ListItem>
  );
};

function isSelectable(imageSet) {
  if (typeof window.package == "undefined") {
    return true;
  }
  const pkg = window.package;

  let allowChange = true;
  if (
    pkg.allow_subject_change == 1 &&
    pkg.subject_uuid != imageSet.subject.uuid
  ) {
    allowChange = false;
  } else if (
    pkg.allow_subject_change == 2 &&
    pkg.job_uuid != imageSet.job.uuid
  ) {
    allowChange = false;
  } else if (
    pkg.allow_subject_change == 3 &&
    (!imageSet.jobType || pkg.job_type_uuid != imageSet.jobType.uuid)
  ) {
    allowChange = false;
  }

  return allowChange;
}

const AvatarWithIcon = ({ imageSet, icon }) => {
  const classes = useStyles();
  let avatar = null;
  let avatarUrl = null;
  if (imageSet?.avatar?.url) {
    avatarUrl = imageSet.avatar.url;
  } else if (icon) {
    avatar = <Icon>{icon}</Icon>;
  } else {
    if (!imageSet?.miniIcon) {
      // We got no icon?
    } else if (imageSet.miniIcon.type == "text") {
      return <StyledMiniIcon>{imageSet.miniIcon.value}</StyledMiniIcon>;
    } else if (imageSet.miniIcon.type == "icon") {
      avatar = (
        <StyledMiniIcon>
          <i className="material-icons">{imageSet.miniIcon.value}</i>
        </StyledMiniIcon>
      );
    }
  }

  console.log("Avatar is", avatar);

  return (
    <Avatar className={classes.avatar} src={avatarUrl}>
      {avatar}
    </Avatar>
  );
};
